import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useSelector } from 'react-redux';
import store, { RootState } from '../../../stores';
import {
  useTracks,
  AudioTrack,
  useConnectionState,
  useParticipants,
  useRoomContext,
} from '@livekit/components-react';
import { ConnectionState, RoomEvent, Track } from 'livekit-client';
import { phaserEvents, Event } from '../../../events/EventCenter';
import { CHARACTER_INITIALIZED } from '../../../managers/InitializationManager';
import { getPhaserGameSync } from '../../../PhaserGame';
import type Game from '../../../scenes/Game';
import Alert from '@mui/material/Alert';
import Slide from '@mui/material/Slide';
import Button from '@mui/material/Button';
import { PermissionsDialog } from '../../PermissionsDialog';
import { LiveKitVideoDisplayProps, VideoControlSurface } from '../types/LiveKitVideoDisplay.types';
import { useVideoDisplayState } from '../hooks/useVideoDisplayState';
import { useLiveKitProximity } from '../context/LiveKitProximityContext';
import { useVideoPositioning } from '../hooks/useVideoPositioning';
import { canPlayersCommunicate } from '../../../services/VirtualRoomProximityHelper';
import { virtualRoomAreaService } from '../../../services/VirtualRoomAreaService';
import { getPlayerPositionForMediaProximity } from '../../../services/getPlayerPositionForMediaProximity';
import { useVirtualRoomFilteredParticipants } from '../../../hooks/useVirtualRoomFilteredParticipants';
import { useAppSelector, useAppDispatch } from '../../../hooks';
import { selectCurrentVirtualRoom, selectRoomOwner } from '../../../stores/VirtualRoomFilterStore';
import { ParticipantVideoOverlay } from '../components/ParticipantVideo';
import { SidebarVideoDisplay } from '../components/SidebarVideoDisplay';
import { GalleryVideoDisplay } from '../components/GalleryVideoDisplay';
import { useGalleryMovementLock } from '../hooks/useGalleryMovementLock';
import { useGalleryCameraQuality } from '../hooks/useGalleryCameraQuality';
import ScreenShareModal from '../../ScreenShareModal';
import WhiteboardModal from '../../WhiteboardModal';
import { addWhiteboardUser, removeWhiteboardUser, setWhiteboardUrls, setWhiteboardVrId } from '../../../stores/WhiteboardStore';
import { ItemType } from '../../../../../types/Items';
import { computeWhiteboardBadgeSet, findWhiteboardForParticipant, detectAutoOpenWhiteboard, isSpuriousRemoval } from '../utils/whiteboardHelpers';
import { LIVEKIT_SCREEN_SHARE_SESSION_ID_EVENT } from '../../LiveKitRoomConnector';
import {
  LIVEKIT_AUDIO_STATE_CHANGED,
  LIVEKIT_VIDEO_STATE_CHANGED,
  LIVEKIT_SCREEN_SHARE_STATE_CHANGED,
} from '../styles/LiveKitVideoDisplay.styles';
import { useLiveKitCallControlsRegistration } from '../context/LiveKitCallControlsContext';
import { RaisedHandsProvider } from '../context/RaisedHandsContext';
import { useOfficeRaisedHands } from '../hooks/useOfficeRaisedHands';
import { useColyseusRoom } from '../../../hooks/useColyseusRoom';
import { useLiveKitSafetyChecker } from '../../../hooks/useLiveKitSafetyChecker';
import { usePictureInPicture } from '../pip/usePictureInPicture';
import { isPipFeatureEnabled } from '../pip/pipCapability';
import { PipHost } from '../pip/PipHost';
import { setVisibilityAwareInterval } from '../../../utils/VisibilityAwareTimer';
import {
  isSettledMediaPeer,
  resolveMediaSubscriptionForParticipant,
  resolveSameVirtualRoomMedia,
} from '../utils/resolveSameVirtualRoomMedia';
import { subscribeNewlyPublishedTrack } from '../utils/subscribeNewlyPublishedTrack';
import { resolveConferenceMediaEligibility } from '../utils/resolveConferenceMediaEligibility';
import { selectAudibleRemoteAudioTracks } from '../utils/selectAudibleRemoteAudioTracks';
import {
  getConferenceAutoMuteThreshold,
  getSubscriptionDebounceMs,
} from '../../../config/mediaConfig';
import { applyConferenceCameraQuality } from '../utils/applyConferenceCameraQuality';
import { currentConferenceMediaTier } from '../utils/currentConferenceMediaTier';

// Debug function to manually emit CHARACTER_INITIALIZED event
function manuallyTriggerCharacterInitialized() {
  phaserEvents.emit(CHARACTER_INITIALIZED, 'manual-trigger');
}

// Add global shortcut for manual trigger
if (typeof window !== 'undefined') {
  (window as any).manuallyTriggerCharacterInitialized = manuallyTriggerCharacterInitialized;
}

// Helper function to check connection state
const isConnectionStateConnected = (state: any): boolean => {
  return state === ConnectionState.Connected;
};

// Add sound notification function
const playScreenShareNotification = () => {
  try {
    const audio = new Audio('/sounds/screenSharingNotification.mp3');
    audio.volume = 0.5; // Set moderate volume
    audio.play().catch((err) => {
      console.warn('Could not play screen share notification sound:', err);
    });
  } catch (err) {
    console.warn('Error creating screen share notification audio:', err);
  }
};

/**
 * Enhanced proximity-aware track subscription manager with best practices
 * 
 * This hook manages LiveKit track subscriptions based on virtual room membership
 * ensuring players only receive audio/video/screen shares from the same virtual room.
 * 
 * Best Practices Implemented:
 * 1. Immediate unsubscription on room transitions (no debounce for isolation)
 * 2. Track publication validation before subscription
 * 3. Reconnection handling to reset state
 * 4. Comprehensive error handling and recovery
 * 5. Cleanup for stale subscriptions
 * 6. Performance monitoring and metrics
 * 7. Room transition detection for immediate updates
 */
/**
 * `galleryTileCountRef` holds the gallery's camera tile count, or null while the gallery is not shown.
 * Every camera subscription here sets the quality cap with it, so re-processing an already-subscribed
 * participant (a share starting, a tab refocus, a reconnect) keeps a small gallery's MEDIUM (SC-375).
 */
const useProximityAwareTrackSubscription = (galleryTileCountRef: React.MutableRefObject<number | null>) => {
  const participants = useParticipants();
  const { inProximityIds } = useLiveKitProximity();
  const isLoading = useSelector((state: RootState) => state.game.loadingGame);
  const virtualRoomFilters = useSelector((state: RootState) => state.virtualRoomFilter.filters);
  const connectionState = useConnectionState();
  const room = useRoomContext();

  // Bug 8 fix: Use refs to avoid stale closures in event handlers and intervals
  const participantsRef = useRef(participants);
  participantsRef.current = participants;
  const inProximityIdsRef = useRef(inProximityIds);
  inProximityIdsRef.current = inProximityIds;

  // Bug 1 fix: Flag to prevent debounced re-subscription during room transitions
  const roomTransitionInProgressRef = useRef(false);
  const roomTransitionTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Track subscription state with metadata for debugging
  const subscriptionStateRef = useRef<Map<string, {
    subscribed: boolean;
    lastUpdate: number;
    trackCount: number;
    roomId?: string; // Track which room the participant was in
    screenSharing?: boolean; // Track if participant is screen sharing
  }>>(new Map());

  // Debounce timer for non-critical subscription changes
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Fast debounce timer for screen share changes (50ms)
  const screenShareDebounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Track last connection state to detect reconnections
  const lastConnectionStateRef = useRef<ConnectionState>(connectionState);

  // Track current room for immediate updates on transitions
  const currentRoomRef = useRef<string | null>(null);

  // Track which participants are screen sharing
  const screenSharingParticipantsRef = useRef<Set<string>>(new Set());

  // Performance metrics
  const metricsRef = useRef({
    subscriptionChanges: 0,
    errors: 0,
    lastResetTime: Date.now(),
    immediateUnsubscribes: 0,
    screenShareChanges: 0
  });

  // Reset subscriptions on reconnection
  useEffect(() => {
    if (lastConnectionStateRef.current === ConnectionState.Reconnecting &&
      connectionState === ConnectionState.Connected) {
      console.log('[LiveKit Track Subscription] Reconnected - resetting subscription state');
      subscriptionStateRef.current.clear();
      currentRoomRef.current = null;
      metricsRef.current.lastResetTime = Date.now();
    }
    lastConnectionStateRef.current = connectionState;
  }, [connectionState]);

  // SC-304: when the tab regains focus, clear subscription tracking so the next
  // proximity cycle forces a full re-subscribe pass. While backgrounded, the
  // underlying WebRTC tracks may have gone stale or been garbage-collected by the
  // browser even though our state still says "subscribed: true". Clearing here
  // ensures every participant is re-evaluated and re-subscribed fresh.
  useEffect(() => {
    const handlePageVisible = () => {
      console.log('[LiveKit Track Subscription] Tab visible - resetting subscription state');
      subscriptionStateRef.current.clear();
      // Trigger immediate proximity recalculation so subscriptions are rebuilt
      // without waiting for the next debounce cycle.
      window.dispatchEvent(new CustomEvent('force-proximity-recalculation'));
    };
    window.addEventListener('page-visible', handlePageVisible);
    return () => window.removeEventListener('page-visible', handlePageVisible);
  }, []);

  // SC-304: when the office Colyseus room drops (WebSocket died but LiveKit
  // survived), unsubscribe all remote tracks to prevent cross-room audio/video
  // leakage. When the office room reconnects, clear subscription state and
  // force a fresh proximity recalculation.
  //
  // Uses room-scoped phaserEvents (roomReconnecting / roomReconnected) instead
  // of the global Redux connectionStatus, which is also written by the lobby
  // socket and InitializationManager — a lobby blip would otherwise cause a
  // spurious full unsubscribe/resubscribe cycle mid-conversation.
  useEffect(() => {
    const unsubscribeAllRemotes = () => {
      console.log('[LiveKit Track Subscription] Colyseus room dropped — unsubscribing remote tracks');
      participantsRef.current.forEach((participant) => {
        if (participant.isLocal) return;
        const trackPublications = Array.from(participant.trackPublications.values() as Iterable<any>);
        trackPublications.forEach((publication) => {
          try {
            publication.setSubscribed(false);
            metricsRef.current.immediateUnsubscribes++;
          } catch (error) {
            console.error('[LiveKit Track Subscription] Unsubscribe failed on room drop:', error);
          }
        });
        subscriptionStateRef.current.delete(`remote-${participant.identity}`);
      });
    };

    const rebuildSubscriptions = () => {
      console.log('[LiveKit Track Subscription] Colyseus room reconnected — rebuilding subscriptions');
      subscriptionStateRef.current.clear();
      window.dispatchEvent(new CustomEvent('force-proximity-recalculation'));
    };

    phaserEvents.on('roomReconnecting', unsubscribeAllRemotes);
    phaserEvents.on('roomReconnected', rebuildSubscriptions);
    return () => {
      phaserEvents.off('roomReconnecting', unsubscribeAllRemotes);
      phaserEvents.off('roomReconnected', rebuildSubscriptions);
    };
  }, []);

  // Listen for virtual room transitions to immediately update subscriptions
  // Bug 8 fix: Use refs to read current participants/inProximityIds to avoid stale closures
  // Bug 1 fix: Set roomTransitionInProgressRef to block debounced re-subscriptions
  useEffect(() => {
    const handleRoomEnter = (event: any) => {
      const phaserGame = getPhaserGameSync();
      if (!phaserGame) return;

      const game = phaserGame.scene?.keys?.game as Game | undefined;
      if (!game || !game.myPlayer) return;

      const newRoomId = event.area?.roomId || event.roomId;
      const previousRoomId = currentRoomRef.current;

      if (newRoomId !== previousRoomId) {
        console.log(`[LiveKit Track Subscription] Room transition detected: ${previousRoomId} -> ${newRoomId}`);
        currentRoomRef.current = newRoomId;

        // Bug 1: Block debounced re-subscriptions during room transition
        roomTransitionInProgressRef.current = true;
        if (roomTransitionTimerRef.current) {
          clearTimeout(roomTransitionTimerRef.current);
        }
        roomTransitionTimerRef.current = setTimeout(() => {
          roomTransitionInProgressRef.current = false;
        }, 200); // 200ms > 100ms debounce to ensure debounce doesn't re-subscribe

        // Bug 8: Read from refs to get current values, not stale closure
        const currentParticipants = participantsRef.current;
        const currentInProximityIds = inProximityIdsRef.current;

        // Immediately unsubscribe from all participants not in proximity
        // This ensures no leakage during transitions
        currentParticipants.forEach((participant) => {
          if (participant.isLocal) return;

          const shouldSubscribe = currentInProximityIds.has(participant.identity);
          const participantKey = `remote-${participant.identity}`;
          const currentState = subscriptionStateRef.current.get(participantKey);

          // If we should NOT subscribe, immediately unsubscribe (no debounce)
          if (!shouldSubscribe && currentState?.subscribed) {
            const trackPublications = Array.from(participant.trackPublications.values() as Iterable<any>);
            trackPublications.forEach((publication) => {
              try {
                publication.setSubscribed(false);
                metricsRef.current.immediateUnsubscribes++;
              } catch (error) {
                console.error(`[LiveKit Track Subscription] Failed to immediately unsubscribe track for ${participant.identity}:`, error);
              }
            });

            subscriptionStateRef.current.set(participantKey, {
              subscribed: false,
              lastUpdate: Date.now(),
              trackCount: trackPublications.length,
              roomId: newRoomId
            });
          }
        });
      }
    };

    const handleLocalRoomLeave = (event: any) => {
      const phaserGame = getPhaserGameSync();
      if (!phaserGame) return;

      const game = phaserGame.scene?.keys?.game as Game | undefined;
      if (!game || !game.myPlayer) return;

      const previousRoomId = event.previousArea?.roomId ?? event.area?.roomId ?? event.roomId;
      if (previousRoomId === currentRoomRef.current) {
        console.log(`[LiveKit Track Subscription] Left room: ${previousRoomId}`);
        currentRoomRef.current = null;

        // Bug 1: Block debounced re-subscriptions during room transition
        roomTransitionInProgressRef.current = true;
        if (roomTransitionTimerRef.current) {
          clearTimeout(roomTransitionTimerRef.current);
        }
        roomTransitionTimerRef.current = setTimeout(() => {
          roomTransitionInProgressRef.current = false;
        }, 200);

        // Bug 8: Read from refs to get current values
        const currentParticipants = participantsRef.current;
        const currentInProximityIds = inProximityIdsRef.current;

        // Immediately unsubscribe from all participants when leaving a room
        currentParticipants.forEach((participant) => {
          if (participant.isLocal) return;

          const shouldSubscribe = currentInProximityIds.has(participant.identity);
          if (!shouldSubscribe) {
            const participantKey = `remote-${participant.identity}`;
            const trackPublications = Array.from(participant.trackPublications.values() as Iterable<any>);
            trackPublications.forEach((publication) => {
              try {
                publication.setSubscribed(false);
                metricsRef.current.immediateUnsubscribes++;
              } catch (error) {
                console.error(`[LiveKit Track Subscription] Failed to immediately unsubscribe on room leave:`, error);
              }
            });

            subscriptionStateRef.current.set(participantKey, {
              subscribed: false,
              lastUpdate: Date.now(),
              trackCount: trackPublications.length,
              roomId: null
            });
          }
        });
      }
    };

    const handleRemoteRoomLeave = (event: any) => {
      const phaserGame = getPhaserGameSync();
      if (!phaserGame) return;

      const game = phaserGame.scene?.keys?.game as Game | undefined;
      const remoteId = event?.playerId as string | undefined;
      const mySid = game?.network?.mySessionId;
      if (!remoteId || !mySid || remoteId === mySid) return;

      const participant = participantsRef.current.find((p) => p.identity === remoteId);
      if (!participant || participant.isLocal) return;

      const participantKey = `remote-${remoteId}`;
      const trackPublications = Array.from(participant.trackPublications.values() as Iterable<any>);
      trackPublications.forEach((publication) => {
        try {
          publication.setSubscribed(false);
          metricsRef.current.immediateUnsubscribes++;
        } catch (error) {
          console.error(
            `[LiveKit Track Subscription] Failed to unsubscribe remote leaver ${remoteId}:`,
            error
          );
        }
      });

      subscriptionStateRef.current.set(participantKey, {
        subscribed: false,
        lastUpdate: Date.now(),
        trackCount: trackPublications.length,
        roomId: currentRoomRef.current,
      });
    };

    // SC-304: when a player's Colyseus session is removed (disconnect, timeout, etc.),
    // immediately unsubscribe their LiveKit tracks. Without this, the proximity system
    // takes multiple cycles (hysteresis + cached positions) to detect the absence,
    // leaving orphaned subscriptions and ghost video tiles.
    const handleRemotePlayerLeft = (playerId: string) => {
      const participant = participantsRef.current.find((p) => p.identity === playerId);
      if (!participant || participant.isLocal) return;

      const participantKey = `remote-${playerId}`;
      const trackPublications = Array.from(participant.trackPublications.values() as Iterable<any>);
      trackPublications.forEach((publication) => {
        try {
          publication.setSubscribed(false);
          metricsRef.current.immediateUnsubscribes++;
        } catch (error) {
          console.error(
            `[LiveKit Track Subscription] Failed to unsubscribe departed player ${playerId}:`,
            error
          );
        }
      });

      subscriptionStateRef.current.delete(participantKey);
    };

    phaserEvents.on(Event.MY_PLAYER_ENTERED_VIRTUAL_ROOM, handleRoomEnter);
    phaserEvents.on(Event.MY_PLAYER_LEFT_VIRTUAL_ROOM, handleLocalRoomLeave);
    phaserEvents.on(Event.PLAYER_ENTERED_VIRTUAL_ROOM, handleRoomEnter);
    phaserEvents.on(Event.PLAYER_LEFT_VIRTUAL_ROOM, handleRemoteRoomLeave);
    phaserEvents.on(Event.PLAYER_LEFT, handleRemotePlayerLeft);

    return () => {
      phaserEvents.off(Event.MY_PLAYER_ENTERED_VIRTUAL_ROOM, handleRoomEnter);
      phaserEvents.off(Event.MY_PLAYER_LEFT_VIRTUAL_ROOM, handleLocalRoomLeave);
      phaserEvents.off(Event.PLAYER_ENTERED_VIRTUAL_ROOM, handleRoomEnter);
      phaserEvents.off(Event.PLAYER_LEFT_VIRTUAL_ROOM, handleRemoteRoomLeave);
      phaserEvents.off(Event.PLAYER_LEFT, handleRemotePlayerLeft);
    };
  }, []); // Bug 8: No dependencies needed since we read from refs

  // Subscription queue for operations during connection issues
  const subscriptionQueueRef = useRef<Array<{ participantId: string; shouldSubscribe: boolean }>>([]);

  // Process subscription queue when connection is stable
  // Bug 2 fix: Re-validate room membership before processing queued subscriptions
  useEffect(() => {
    if (connectionState === ConnectionState.Connected && subscriptionQueueRef.current.length > 0) {
      console.log(`[LiveKit Track Subscription] Processing ${subscriptionQueueRef.current.length} queued subscription operations`);

      const queue = [...subscriptionQueueRef.current];
      subscriptionQueueRef.current = [];

      // Bug 2: Get current room context to re-validate membership
      const phaserGame = getPhaserGameSync();
      const game = phaserGame?.scene?.keys?.game as Game | undefined;
      const myPlayer = game?.myPlayer;
      const myArea = myPlayer ? virtualRoomAreaService.isPlayerInArea(myPlayer.x, myPlayer.y) : null;
      const myRoomId = myArea?.roomId || null;

      queue.forEach(({ participantId, shouldSubscribe }) => {
        const participant = participants.find(p => p.identity === participantId);
        if (!participant || participant.isLocal) return;

        // Bug 2: Re-validate room membership - queued subscription may be stale
        let validatedShouldSubscribe = shouldSubscribe;
        if (shouldSubscribe && myPlayer && game) {
          validatedShouldSubscribe = resolveMediaSubscriptionForParticipant(
            game,
            participantId,
            shouldSubscribe,
            virtualRoomFilters,
            { isScreenSharing: participant.isScreenShareEnabled }
          );
        }

        const trackPublications = Array.from(participant.trackPublications.values() as Iterable<any>);
        trackPublications.forEach((publication: any) => {
          try {
            publication.setSubscribed(validatedShouldSubscribe);
          } catch (error) {
            console.error(`[LiveKit Track Subscription] Failed to process queued subscription for ${participantId}:`, error);
          }
        });
      });
    }
  }, [connectionState, participants]);

  // Main subscription logic with debouncing
  useEffect(() => {
    if (isLoading) {
      subscriptionStateRef.current.clear();
      return;
    }

    // Don't process subscriptions if connection is not stable
    if (connectionState !== ConnectionState.Connected) {
      // Queue subscription operations for when connection is restored
      return;
    }

    // Clear any pending debounce timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Both subscribe and unsubscribe now go through the debounce (300ms).
    // Previously, unsubscribes were immediate while subscribes were debounced —
    // a race where a transient Phaser position read during player-join would
    // immediately unsubscribe video tracks without a paired resubscription.
    // Room-transition events (phaserEvents handlers above) still unsubscribe
    // immediately since those are deliberate and authoritative.
    //
    // Debounce subscription changes (both subscribe AND unsubscribe); tier scales with talk size.
    const { tier: mediaTier } = currentConferenceMediaTier();
    const subscriptionDebounceMs = getSubscriptionDebounceMs(mediaTier);

    debounceTimerRef.current = setTimeout(() => {
      // Bug 1 fix: Skip debounced subscription if a room transition just happened
      if (roomTransitionInProgressRef.current) {
        console.log('[LiveKit Track Subscription] Skipping debounced subscription - room transition in progress');
        return;
      }

      const now = Date.now();
      const currentParticipantIds = new Set<string>();

      // Get current room context for isolation checks
      const phaserGame = getPhaserGameSync();
      const game = phaserGame?.scene?.keys?.game as Game | undefined;
      const myPlayer = game?.myPlayer;
      const myArea = myPlayer ? virtualRoomAreaService.isPlayerInArea(myPlayer.x, myPlayer.y) : null;
      const myRoomId = myArea?.roomId || null;

      participants.forEach((participant) => {
        if (participant.isLocal) {
          return; // Local participant always has all tracks
        }

        currentParticipantIds.add(participant.identity);
        const shouldSubscribe = inProximityIds.has(participant.identity);
        const participantKey = `remote-${participant.identity}`;
        const currentState = subscriptionStateRef.current.get(participantKey);

        let finalShouldSubscribe = shouldSubscribe;
        if (myPlayer && game) {
          finalShouldSubscribe = resolveMediaSubscriptionForParticipant(
            game,
            participant.identity,
            shouldSubscribe,
            virtualRoomFilters,
            { isScreenSharing: participant.isScreenShareEnabled }
          );
        }

        // Track if participant is screen sharing
        const isScreenSharing = participant.isScreenShareEnabled;
        const wasScreenSharing = currentState?.screenSharing || false;
        const screenShareChanged = isScreenSharing !== wasScreenSharing;

        if (isScreenSharing) {
          screenSharingParticipantsRef.current.add(participant.identity);
        } else {
          screenSharingParticipantsRef.current.delete(participant.identity);
        }

        // Only update if state has changed
        if (!currentState || currentState.subscribed !== finalShouldSubscribe || screenShareChanged) {
          // Validate participant has track publications before attempting subscription
          const trackPublications = Array.from(participant.trackPublications.values() as Iterable<any>);

          if (trackPublications.length === 0) {
            // No tracks available - skip without logging (too frequent)
            return;
          }

          // Separate tracks by type for prioritization
          const microphoneTracks: any[] = [];
          const cameraTracks: any[] = [];
          const screenShareTracks: any[] = [];
          const screenShareAudioTracks: any[] = [];
          const otherTracks: any[] = [];

          trackPublications.forEach((publication: any) => {
            if (!publication) return;

            // Validate publication is ready
            if (publication.track === null && !finalShouldSubscribe) {
              return; // Track not yet available, skip
            }

            // Categorize tracks by source for prioritization
            if (publication.source === Track.Source.Microphone) {
              microphoneTracks.push(publication);
            } else if (publication.source === Track.Source.Camera) {
              cameraTracks.push(publication);
            } else if (publication.source === Track.Source.ScreenShare) {
              screenShareTracks.push(publication);
            } else if (publication.source === Track.Source.ScreenShareAudio) {
              screenShareAudioTracks.push(publication);
            } else {
              otherTracks.push(publication);
            }
          });

          let successCount = 0;
          let errorCount = 0;

          // Priority-based subscription: Microphone > Camera > Screen Share > Screen Share Audio > Others
          // Subscribe in priority order
          const prioritizedTracks = [
            ...microphoneTracks,
            ...cameraTracks,
            ...screenShareTracks,
            ...screenShareAudioTracks,
            ...otherTracks
          ];

          prioritizedTracks.forEach((publication) => {
            try {
              // Check connection state before subscription operations
              if (connectionState !== ConnectionState.Connected) {
                // Queue for later processing
                if (!subscriptionQueueRef.current.find(q => q.participantId === participant.identity)) {
                  subscriptionQueueRef.current.push({
                    participantId: participant.identity,
                    shouldSubscribe: finalShouldSubscribe
                  });
                }
                return;
              }



              // Subscribe/unsubscribe based on virtual room membership
              // CRITICAL: Unsubscribe immediately if isolation rules require it
              publication.setSubscribed(finalShouldSubscribe);
              if (finalShouldSubscribe) {
                applyConferenceCameraQuality(publication, mediaTier, galleryTileCountRef.current);
              }
              successCount++;

              // Only log in development mode and only on state changes (not every check)
              // This prevents performance degradation from excessive logging
            } catch (error) {
              errorCount++;
              console.error(`[LiveKit Track Subscription] Failed to ${finalShouldSubscribe ? 'subscribe' : 'unsubscribe'} ${publication.source} track for ${participant.identity}:`, error);
              metricsRef.current.errors++;

              // Attempt error recovery: queue for retry
              if (connectionState === ConnectionState.Connected) {
                if (!subscriptionQueueRef.current.find(q => q.participantId === participant.identity)) {
                  subscriptionQueueRef.current.push({
                    participantId: participant.identity,
                    shouldSubscribe: finalShouldSubscribe
                  });
                }
              }
            }
          });

          // Update subscription state with room context and screen share status
          subscriptionStateRef.current.set(participantKey, {
            subscribed: finalShouldSubscribe,
            lastUpdate: now,
            trackCount: trackPublications.length,
            roomId: myRoomId,
            screenSharing: isScreenSharing
          });

          metricsRef.current.subscriptionChanges++;
          if (screenShareChanged) {
            metricsRef.current.screenShareChanges++;
          }

          // Only log in development and only significant changes
          // Removed per-participant logging to improve performance
        }
      });

      // Clean up stale subscriptions for participants who left
      const keysToRemove: string[] = [];
      subscriptionStateRef.current.forEach((state, key) => {
        const participantId = key.replace('remote-', '');
        if (!currentParticipantIds.has(participantId)) {
          keysToRemove.push(key);
        }
      });

      if (keysToRemove.length > 0) {
        keysToRemove.forEach(key => {
          subscriptionStateRef.current.delete(key);
        });
        // Removed log to improve performance
      }

      // CRITICAL: Clean up stale tracks from participants in different rooms.
      subscriptionStateRef.current.forEach((state, key) => {
        const participantId = key.replace('remote-', '');
        const participant = participants.find(p => p.identity === participantId);

        if (participant && !participant.isLocal && game) {
          if (resolveConferenceMediaEligibility(game, participantId, virtualRoomFilters)) {
            return;
          }

          const otherPos = getPlayerPositionForMediaProximity(game, participantId);
          if (!otherPos) return;

          const otherArea = virtualRoomAreaService.isPlayerInArea(otherPos.x, otherPos.y);
          const otherRoomId = otherArea?.roomId || null;

          if (myRoomId !== null && otherRoomId !== null && myRoomId !== otherRoomId) {
            if (state.subscribed) {
              const trackPublications = Array.from(participant.trackPublications.values() as Iterable<any>);
              trackPublications.forEach((publication) => {
                try {
                  publication.setSubscribed(false);
                  metricsRef.current.immediateUnsubscribes++;
                } catch (error) {
                  console.error(`[LiveKit Track Subscription] Failed to cleanup stale track:`, error);
                }
              });

              subscriptionStateRef.current.set(key, {
                subscribed: false,
                lastUpdate: Date.now(),
                trackCount: trackPublications.length,
                roomId: myRoomId
              });
            }
          }
        }
      });

      // Log metrics only once per minute to avoid performance impact
      if (process.env.NODE_ENV === 'development' && now - metricsRef.current.lastResetTime > 60000) {
        console.log('[LiveKit Track Subscription] Metrics (last 60s):', {
          subscriptionChanges: metricsRef.current.subscriptionChanges,
          errors: metricsRef.current.errors,
          activeSubscriptions: subscriptionStateRef.current.size
        });
        // Reset metrics
        metricsRef.current.subscriptionChanges = 0;
        metricsRef.current.errors = 0;
        metricsRef.current.lastResetTime = now;
      }

    }, subscriptionDebounceMs);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [participants, inProximityIds, isLoading, connectionState, virtualRoomFilters]);

  // Fast-track screen share changes with reduced debounce (50ms)
  useEffect(() => {
    if (isLoading || connectionState !== ConnectionState.Connected) {
      return;
    }

    // Clear any pending screen share debounce timer
    if (screenShareDebounceTimerRef.current) {
      clearTimeout(screenShareDebounceTimerRef.current);
    }

    // Check for screen share state changes
    const hasScreenShareChanges = Array.from(participants).some((participant) => {
      if (participant.isLocal) return false;
      const participantKey = `remote-${participant.identity}`;
      const currentState = subscriptionStateRef.current.get(participantKey);
      const isScreenSharing = participant.isScreenShareEnabled;
      return currentState?.screenSharing !== isScreenSharing;
    });

    if (hasScreenShareChanges) {
      // Use faster debounce for screen share changes (50ms)
      screenShareDebounceTimerRef.current = setTimeout(() => {
        const phaserGame = getPhaserGameSync();
        if (!phaserGame) return;

        const game = phaserGame.scene?.keys?.game as Game | undefined;
        const myPlayer = game?.myPlayer;
        if (!myPlayer) return;

        participants.forEach((participant) => {
          if (participant.isLocal) return;

          const participantKey = `remote-${participant.identity}`;
          const currentState = subscriptionStateRef.current.get(participantKey);
          const isScreenSharing = participant.isScreenShareEnabled;
          const wasScreenSharing = currentState?.screenSharing || false;

          if (isScreenSharing !== wasScreenSharing) {
            const shouldSubscribe = inProximityIds.has(participant.identity);
            const shouldSubscribeScreenShare = game
              ? resolveMediaSubscriptionForParticipant(
                game,
                participant.identity,
                shouldSubscribe,
                virtualRoomFilters,
                { isScreenSharing: true }
              )
              : false;

            if (shouldSubscribeScreenShare) {
              const trackPublications = Array.from(participant.trackPublications.values() as Iterable<any>);

              trackPublications.forEach((publication: any) => {
                if (publication.source === Track.Source.ScreenShare || publication.source === Track.Source.ScreenShareAudio) {
                  try {
                    if (isScreenSharing) {
                      publication.setSubscribed(true);
                    } else {
                      publication.setSubscribed(false);
                    }
                  } catch (error) {
                    console.error(`[LiveKit Track Subscription] Failed to update screen share track for ${participant.identity}:`, error);
                  }
                }
              });

              // Update state
              subscriptionStateRef.current.set(participantKey, {
                subscribed: currentState?.subscribed || false,
                lastUpdate: Date.now(),
                trackCount: trackPublications.length,
                roomId: currentState?.roomId,
                screenSharing: isScreenSharing
              });
            }
          }
        });
      }, 50); // 50ms debounce for screen share changes
    }

    return () => {
      if (screenShareDebounceTimerRef.current) {
        clearTimeout(screenShareDebounceTimerRef.current);
      }
    };
  }, [participants, inProximityIds, isLoading, connectionState, virtualRoomFilters]);

  // Screen share heartbeat: every 5 seconds, re-assert subscriptions for any
  // participant who is actively screen sharing AND in proximity AND in the same room.
  // This handles the "video/screen disappears after some time" scenario where a track
  // gets silently unsubscribed but the state machine misses it.
  const heartbeatParticipantsRef = useRef(participants);
  heartbeatParticipantsRef.current = participants;
  const heartbeatInProximityRef = useRef(inProximityIds);
  heartbeatInProximityRef.current = inProximityIds;

  useEffect(() => {
    if (connectionState !== ConnectionState.Connected) return;

    const screenShareHeartbeat = () => {
      const currentParticipants = heartbeatParticipantsRef.current;
      const currentInProximityIds = heartbeatInProximityRef.current;

      // CRITICAL ROOM ISOLATION: Get local player's area once per heartbeat tick
      const phaserGame = getPhaserGameSync();
      const game = phaserGame?.scene?.keys?.game as Game | undefined;
      const myPlayer = game?.myPlayer;
      const myArea = myPlayer ? virtualRoomAreaService.isPlayerInArea(myPlayer.x, myPlayer.y) : null;

      currentParticipants.forEach((participant) => {
        if (participant.isLocal || !participant.isScreenShareEnabled) return;
        if (!game || !myPlayer) return;

        const shouldSubscribeScreenShare = resolveMediaSubscriptionForParticipant(
          game,
          participant.identity,
          currentInProximityIds.has(participant.identity),
          virtualRoomFilters,
          { isScreenSharing: true }
        );

        if (!shouldSubscribeScreenShare) {
          const trackPublications = Array.from(participant.trackPublications.values() as Iterable<any>);
          trackPublications.forEach((publication: any) => {
            if (publication.source !== Track.Source.ScreenShare &&
              publication.source !== Track.Source.ScreenShareAudio) return;
            try { publication.setSubscribed(false); } catch { /* ignore */ }
          });
          return;
        }

        const trackPublications = Array.from(participant.trackPublications.values() as Iterable<any>);
        trackPublications.forEach((publication: any) => {
          if (publication.source !== Track.Source.ScreenShare &&
            publication.source !== Track.Source.ScreenShareAudio) return;

          if (!publication.isSubscribed) {
            try {
              publication.setSubscribed(true);
              console.log(`[LiveKit Heartbeat] Re-subscribed screen share track for ${participant.identity}`);
            } catch (err) {
              console.warn(`[LiveKit Heartbeat] Failed to re-subscribe screen share:`, err);
            }
          }
        });
      });
    };

    const cleanup = setVisibilityAwareInterval(screenShareHeartbeat, 5000, 10000);
    return cleanup;
  }, [connectionState, virtualRoomFilters]);

  // SC-246 (Issues 1 & 3): subscribe newly-published tracks immediately.
  // autoSubscribe is off and the debounced loop above keys on a coarse
  // per-participant `subscribed` boolean, so a camera published AFTER we first
  // subscribed a participant (join race, or camera toggled on later) would never
  // be subscribed -> black tile for some viewers until a rejoin/toggle. Reacting
  // to TrackPublished closes that gap while respecting the same proximity + room
  // isolation + password decision used everywhere else.
  useEffect(() => {
    if (!room) return;

    const handleTrackPublished = (publication: any, participant: any) => {
      if (!participant || participant.isLocal) return;
      if (connectionState !== ConnectionState.Connected) return;

      const phaserGame = getPhaserGameSync();
      const game = phaserGame?.scene?.keys?.game as Game | undefined;
      if (!game || !game.myPlayer) return;

      const isScreenShareSource =
        publication?.source === Track.Source.ScreenShare ||
        publication?.source === Track.Source.ScreenShareAudio;

      const shouldSubscribe = resolveMediaSubscriptionForParticipant(
        game,
        participant.identity,
        inProximityIdsRef.current.has(participant.identity),
        virtualRoomFilters,
        { isScreenSharing: isScreenShareSource }
      );

      const didSubscribe = subscribeNewlyPublishedTrack(publication, shouldSubscribe);
      if (didSubscribe && publication?.source === Track.Source.Camera) {
        applyConferenceCameraQuality(publication, currentConferenceMediaTier().tier, galleryTileCountRef.current);
      }
    };

    room.on(RoomEvent.TrackPublished, handleTrackPublished);
    return () => {
      room.off(RoomEvent.TrackPublished, handleTrackPublished);
    };
  }, [room, connectionState, virtualRoomFilters]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      if (screenShareDebounceTimerRef.current) {
        clearTimeout(screenShareDebounceTimerRef.current);
      }
      // Bug 1: Clear room transition timer on unmount
      if (roomTransitionTimerRef.current) {
        clearTimeout(roomTransitionTimerRef.current);
      }
      subscriptionStateRef.current.clear();
      subscriptionQueueRef.current = [];
      screenSharingParticipantsRef.current.clear();
    };
  }, []);
};

// Enhanced proximity-aware audio renderer with proper LiveKit integration
const ProximityAwareRoomAudioRenderer: React.FC<{
  isParticipantInSameRoom: (participantId: string) => boolean;
}> = ({ isParticipantInSameRoom }) => {
  const microphoneTracks = useTracks([Track.Source.Microphone]);
  const screenShareAudioTracks = useTracks([Track.Source.ScreenShareAudio]);
  const { inProximityIds } = useLiveKitProximity();

  // NOTE: Do NOT call useProximityAwareTrackSubscription() here.
  // It is already instantiated in LiveKitVideoDisplayController (which renders
  // this component). Two independent instances each have their own
  // subscriptionStateRef and will fight each other — one subscribes, the other
  // unsubscribes — causing screen share and camera tracks to disappear randomly.

  const validAudioTracks = React.useMemo(
    () =>
      selectAudibleRemoteAudioTracks(microphoneTracks, screenShareAudioTracks, {
        isInProximity: (identity) => inProximityIds.has(identity),
        canSeeScreenShare: isParticipantInSameRoom,
      }),
    [microphoneTracks, screenShareAudioTracks, inProximityIds, isParticipantInSameRoom]
  );

  return (
    <>
      {validAudioTracks.map((track) => (
        <div key={`audio-${track.participant.identity}-${track.publication?.trackSid || (track.publication?.track as any)?.sid || 'unknown'}`}>
          {/* @ts-ignore - AudioTrack returns ReactNode but we need JSX.Element */}
          <AudioTrack trackRef={track} />
        </div>
      ))}
    </>
  );
};

// Add comprehensive connection health monitoring
// Bug 9 fix: Use refs to avoid stale closures in interval callback
const useConnectionHealthMonitoring = () => {
  const participants = useParticipants();
  const { inProximityIds } = useLiveKitProximity();
  const connectionState = useConnectionState();

  // Bug 9: Keep refs updated to avoid stale closures in the interval
  const participantsRef = useRef(participants);
  participantsRef.current = participants;
  const inProximityIdsRef = useRef(inProximityIds);
  inProximityIdsRef.current = inProximityIds;

  useEffect(() => {
    const checkConnectionHealth = () => {
      const issues = [];

      // Get game instance
      const phaserGame = getPhaserGameSync();
      if (!phaserGame) return;

      const game = phaserGame.scene?.getScene('Game') as Game | undefined;
      if (!game || !game.myPlayer) return;

      const myPlayer = game.myPlayer;

      // Bug 9: Read from refs to get current values
      const currentParticipants = participantsRef.current;
      const currentInProximityIds = inProximityIdsRef.current;

      // Check for participants not in proximity but should be
      currentParticipants.forEach((participant) => {
        if (!participant.isLocal) {
          const otherPos = getPlayerPositionForMediaProximity(game, participant.identity);
          if (otherPos) {
            const shouldBeInProximity = canPlayersCommunicate(
              { x: myPlayer.x, y: myPlayer.y, playerId: myPlayer.playerId },
              { x: otherPos.x, y: otherPos.y, playerId: otherPos.playerId },
              100
            );

            if (shouldBeInProximity && !currentInProximityIds.has(participant.identity)) {
              issues.push(`Participant ${participant.identity} should be in proximity but isn't`);
            }
          }
        }
      });

      // Check for audio track subscription issues
      currentParticipants.forEach((participant) => {
        if (!participant.isLocal) {
          const audioTracks = Array.from(participant.trackPublications.values() as any)
            .filter((pub: any) => pub.kind === 'audio');

          if (audioTracks.length > 0 && currentInProximityIds.has(participant.identity)) {
            const subscribedAudioTracks = audioTracks.filter((pub: any) => pub.isSubscribed);

            if (subscribedAudioTracks.length === 0) {
              issues.push(`Participant ${participant.identity} has audio but not subscribed`);
            }
          }
        }
      });

      if (issues.length > 0) {
        console.warn('[LiveKit Health] Connection issues detected:', issues);
        window.dispatchEvent(new CustomEvent('force-proximity-recalculation'));
      }
    };

    const healthCleanup = setVisibilityAwareInterval(checkConnectionHealth, 2000, 5000);
    return healthCleanup;
  }, [connectionState]); // Bug 9: Removed participants/inProximityIds from deps since we use refs
};

export const LiveKitVideoDisplayController: React.FC<LiveKitVideoDisplayProps> = (props) => {
  const {
    isVisible = true,
    connectionState: propConnectionState,
    isCharacterInitialized: propIsCharacterInitialized,
    isLoading: propIsLoading,
  } = props;

  // Initialize safety checker
  const safetyChecker = useLiveKitSafetyChecker({
    autoStart: true,
    checkInterval: 3000,
    autoFixEnabled: true,
    enableAlerts: true,
    onHealthChange: (healthScore) => {
      console.log(`[LiveKit Safety] Health score changed to: ${healthScore}`);
    },
    onIssueDetected: (issue) => {
      console.warn(`[LiveKit Safety] Issue detected:`, issue);
    },
    onFixApplied: (fix) => {
      console.log(`[LiveKit Safety] Fix applied: ${fix}`);
    }
  });

  const connectionState = useConnectionState();
  const dispatch = useAppDispatch();
  const [isCharacterInitialized, setIsCharacterInitialized] = useState(false);

  // Use filtered participants and tracks from centralized hook
  const {
    participants,
    screenShareTracks,
    cameraTracks: tracks,
    microphoneTracks: filteredMicrophoneTracks,
    isParticipantInSameRoom
  } = useVirtualRoomFilteredParticipants();

  // Room owner for showing owner badge on name badges
  const currentRoom = useAppSelector(selectCurrentVirtualRoom);
  const ownerSessionId = useAppSelector((state) =>
    currentRoom ? selectRoomOwner(state, currentRoom.roomId) : undefined
  );
  const virtualRoomFiltersForUi = useSelector((state: RootState) => state.virtualRoomFilter.filters);
  const activeWhiteboardUsers = useSelector((state: RootState) => state.whiteboard.activeWhiteboardUsers);
  const whiteboardVrIds = useSelector((state: RootState) => state.whiteboard.whiteboardVrIds);
  const whiteboardUrls = useSelector((state: RootState) => state.whiteboard.urls);

  // Use our custom hooks for state management
  const [videoDisplayState, videoDisplayActions] = useVideoDisplayState();


  // SC-209: one application-wide switch decides whether this is offered at all; the hook then decides what
  // this browser can actually do with it. `open`/`close` keep a stable identity, which is what lets them go
  // on the toolbar surface below, long before the model inputs they need exist further down this component.
  const workspaceSettings = useSelector((state: RootState) => state.workspace.settings);
  const roomName = useSelector((state: RootState) => state.room.roomName);
  const pip = usePictureInPicture({ enabled: isPipFeatureEnabled(workspaceSettings) });

  // Build a set of session IDs currently using any whiteboard (for badge rendering).
  // Only include users from VR whiteboards in the SAME virtual room as the local user.
  // Public whiteboards (no vrId) are excluded — they don't show remote badges.
  const localVrId = currentRoom?.roomId || null
  const whiteboardUserSessionIds = useMemo(
    () => computeWhiteboardBadgeSet(activeWhiteboardUsers, whiteboardVrIds, localVrId),
    [activeWhiteboardUsers, whiteboardVrIds, localVrId],
  )

  // Handle whiteboard badge click: find the whiteboard for this participant and maximize
  const handleWhiteboardClick = useCallback((participantId: string) => {
    const wbId = findWhiteboardForParticipant(activeWhiteboardUsers, participantId)
    if (!wbId) return
    let url = whiteboardUrls[wbId]
    // Generate URL on-the-fly if not yet in store (remote client ID mismatch)
    if (!url) {
      const roomId = store.getState().room.roomId
      if (roomId) {
        dispatch(setWhiteboardUrls({ whiteboardId: wbId, roomId }))
        url = store.getState().whiteboard.urls[wbId]
      }
    }
    if (url) {
      videoDisplayActions.maximizeWhiteboard(wbId, participantId, url)
    }
  }, [activeWhiteboardUsers, whiteboardUrls, videoDisplayActions, dispatch]);
  const { setSurface } = useLiveKitCallControlsRegistration();

  const proximityState = useLiveKitProximity();

  // SC-415: one raise-hand instance for the office — call bar, tiles and both presentation views.
  const colyseusRoom = useColyseusRoom();
  const isSettledRaiseHandPeer = useCallback(
    (identity: string) =>
      isSettledMediaPeer(getPhaserGameSync()?.scene?.keys?.game as Game | undefined, identity, virtualRoomFiltersForUi),
    [virtualRoomFiltersForUi]
  );
  const { shared: raisedHands, canLowerHands } = useOfficeRaisedHands({
    room: colyseusRoom,
    inProximityIds: proximityState.inProximityIds,
    localIdentity: participants.find((p) => p.isLocal)?.identity,
    isSettledPeer: isSettledRaiseHandPeer,
    ownerSessionId,
    virtualRoomId: currentRoom?.roomId ?? null,
  });
  const lowerOthersHand = canLowerHands ? raisedHands.lowerHand : undefined;

  const controlSurface = useMemo((): VideoControlSurface => ({
    toggleAudio: videoDisplayActions.toggleAudio,
    toggleVideo: videoDisplayActions.toggleVideo,
    toggleScreenShare: videoDisplayActions.toggleScreenShare,
    toggleDisplayMode: videoDisplayActions.toggleDisplayMode,
    setDisplayMode: videoDisplayActions.setDisplayMode,
    audioEnabled: videoDisplayState.audioEnabled,
    videoEnabled: videoDisplayState.videoEnabled,
    screenShareEnabled: videoDisplayState.screenShareEnabled,
    maximizeScreenShare: videoDisplayActions.maximizeScreenShare,
    maximizedScreenShare: videoDisplayState.maximizedScreenShare,
    isAutoMuted: videoDisplayState.isAutoMuted,
    autoMuteReason: videoDisplayState.autoMuteReason,
    displayMode: videoDisplayState.displayMode,
    raiseHand: raisedHands.canRaiseHand
      ? { raised: raisedHands.localHandRaised, toggle: raisedHands.toggleHandRaise }
      : undefined,
    pictureInPicture: {
      mode: pip.mode,
      isOpen: pip.isOpen,
      canOpen: pip.canOpen,
      open: pip.open,
      close: pip.close,
    },
  }), [
    videoDisplayActions.toggleAudio,
    videoDisplayActions.toggleVideo,
    videoDisplayActions.toggleScreenShare,
    videoDisplayActions.toggleDisplayMode,
    videoDisplayActions.setDisplayMode,
    videoDisplayActions.maximizeScreenShare,
    videoDisplayState.audioEnabled,
    videoDisplayState.videoEnabled,
    videoDisplayState.screenShareEnabled,
    videoDisplayState.displayMode,
    videoDisplayState.isAutoMuted,
    videoDisplayState.autoMuteReason,
    videoDisplayState.maximizedScreenShare,
    raisedHands.canRaiseHand,
    raisedHands.localHandRaised,
    raisedHands.toggleHandRaise,
    pip.mode,
    pip.isOpen,
    pip.canOpen,
    pip.open,
    pip.close,
  ]);

  useEffect(() => {
    setSurface(controlSurface);
  }, [setSurface, controlSurface]);

  useEffect(() => {
    return () => setSurface(null);
  }, [setSurface]);

  const positioningState = useVideoPositioning();

  // Add loading state check to prevent rendering during loading
  const isLoading = useSelector((state: RootState) => state.game.loadingGame);

  // SC-375: the gallery's effects follow whether it is on screen, not just chosen. The render gate below
  // hides it during a LiveKit reconnect or a loading screen, and movement must not stay locked while the
  // player is looking at the map.
  const galleryShown =
    videoDisplayState.displayMode === 'gallery' &&
    connectionState === ConnectionState.Connected &&
    isCharacterInitialized &&
    isVisible &&
    !isLoading;
  useGalleryMovementLock(galleryShown);
  const [galleryTileCount, setGalleryTileCount] = useState<number | null>(null);
  const activeGalleryTileCount = galleryShown ? galleryTileCount : null;
  const galleryTileCountRef = useRef<number | null>(activeGalleryTileCount);
  galleryTileCountRef.current = activeGalleryTileCount;
  const liveKitRoom = useRoomContext();
  useGalleryCameraQuality(liveKitRoom, activeGalleryTileCount);

  // CRITICAL: Manage track subscriptions based on virtual room membership
  // This ensures players only receive audio/video/screen shares from players in the same virtual room
  useProximityAwareTrackSubscription(galleryTileCountRef);

  // Add connection health monitoring
  useConnectionHealthMonitoring();

  // Handle speaking state changes
  useEffect(() => {
    positioningState.handleSpeakingStateChange(proximityState.speakingIds);
  }, [proximityState.speakingIds, positioningState.handleSpeakingStateChange]);

  // Handle non-speaking videos
  useEffect(() => {
    const cleanup = positioningState.handleNonSpeakingVideos(proximityState.speakingIds);
    return cleanup;
  }, [proximityState.speakingIds, positioningState.handleNonSpeakingVideos]);

  // Error banner state
  const errorTimerRef = useRef<NodeJS.Timeout | null>(null);
  const connectionCheckIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Refs to avoid re-dispatching the same participant state (stops audio/video sync loop)
  const lastSyncedAudioRef = useRef<boolean | undefined>(undefined);
  const lastSyncedVideoRef = useRef<boolean | undefined>(undefined);
  const lastSyncedScreenShareRef = useRef<boolean | undefined>(undefined);

  // Call Stability Fix: "Recently seen" grace - keep participants visible for 3s after leaving proximity
  const RECENTLY_SEEN_GRACE_MS = 3000;
  const recentlySeenInProximityRef = useRef<Map<string, number>>(new Map());
  // Read by the predicate below: a sweep that prunes an entry has to give it a new identity, or every memo
  // built on it (the sidebar's list, the PiP window's tiles) keeps showing someone who walked away.
  const [graceRevision, setGraceRevision] = useState(0);
  useEffect(() => {
    const now = Date.now();
    proximityState.inProximityIds.forEach((id) => {
      recentlySeenInProximityRef.current.set(id, now);
    });
    let pruned = false;
    recentlySeenInProximityRef.current.forEach((timestamp, id) => {
      if (now - timestamp > RECENTLY_SEEN_GRACE_MS) {
        recentlySeenInProximityRef.current.delete(id);
        pruned = true;
      }
    });
    if (pruned) setGraceRevision((r) => r + 1);
  }, [proximityState.inProximityIds]);
  useEffect(() => {
    const graceCheckCleanup = setVisibilityAwareInterval(() => {
      const now = Date.now();
      let pruned = false;
      recentlySeenInProximityRef.current.forEach((timestamp, id) => {
        if (now - timestamp > RECENTLY_SEEN_GRACE_MS) {
          recentlySeenInProximityRef.current.delete(id);
          pruned = true;
        }
      });
      if (pruned) setGraceRevision((r) => r + 1);
    }, 500, 2000);
    return graceCheckCleanup;
  }, []);
  const isParticipantVisibleWithGrace = useCallback((participantId: string) => {
    if (proximityState.inProximityIds.has(participantId)) return true;
    const lastSeen = recentlySeenInProximityRef.current.get(participantId);
    return lastSeen != null && (Date.now() - lastSeen) < RECENTLY_SEEN_GRACE_MS;
  }, [proximityState.inProximityIds, graceRevision]);

  // Update audio/video state based on actual tracks (only when participant state changes, not when display state changes)
  useEffect(() => {
    if (participants.length === 0) {
      lastSyncedAudioRef.current = undefined;
      lastSyncedVideoRef.current = undefined;
      lastSyncedScreenShareRef.current = undefined;
      return;
    }
    const localParticipant = participants.find((p) => p.isLocal);
    if (!localParticipant) {
      lastSyncedAudioRef.current = undefined;
      lastSyncedVideoRef.current = undefined;
      lastSyncedScreenShareRef.current = undefined;
      return;
    }
    const hasAudio = localParticipant.isMicrophoneEnabled;
    const hasVideo = localParticipant.isCameraEnabled;
    const hasScreenShare = localParticipant.isScreenShareEnabled;

    if (hasAudio !== lastSyncedAudioRef.current) {
      lastSyncedAudioRef.current = hasAudio;
      window.dispatchEvent(
        new CustomEvent(LIVEKIT_AUDIO_STATE_CHANGED, {
          detail: { enabled: hasAudio },
        })
      );
    }
    if (hasVideo !== lastSyncedVideoRef.current) {
      lastSyncedVideoRef.current = hasVideo;
      window.dispatchEvent(
        new CustomEvent(LIVEKIT_VIDEO_STATE_CHANGED, {
          detail: { enabled: hasVideo },
        })
      );
    }
    if (hasScreenShare !== lastSyncedScreenShareRef.current) {
      lastSyncedScreenShareRef.current = hasScreenShare;
      window.dispatchEvent(
        new CustomEvent(LIVEKIT_SCREEN_SHARE_STATE_CHANGED, {
          detail: { enabled: hasScreenShare },
        })
      );
    }
  }, [participants]);

  // Add a special trigger that fires when connection state becomes Connected
  useEffect(() => {
    if (connectionState === ConnectionState.Connected) {
      // After 2 seconds of being connected, manually trigger character initialization
      // This helps in cases where the normal event flow might be broken
      const timeoutId = setTimeout(() => {
        if (!isCharacterInitialized) {
          setIsCharacterInitialized(true);
        }
      }, 2000);

      return () => clearTimeout(timeoutId);
    }
  }, [connectionState, isCharacterInitialized]);

  // Auto-mute mic in large VR talks (threshold scales with conference tier).
  useEffect(() => {
    if (!isCharacterInitialized || connectionState !== ConnectionState.Connected) {
      return;
    }

    const handleProximityAreaChange = (event: CustomEvent) => {
      const { currentArea } = event.detail;

      if (currentArea) {
        const { tier, vrOccupantCount } = currentConferenceMediaTier();
        const autoMuteThreshold = getConferenceAutoMuteThreshold(tier);

        if (vrOccupantCount >= autoMuteThreshold) {
          const localParticipant = participants.find((p) => p.isLocal);

          if (localParticipant && localParticipant.isMicrophoneEnabled && videoDisplayState.audioEnabled) {
            videoDisplayActions.toggleAudio();
          }
        }
      }
    };

    // Add event listener for proximity area changes
    window.addEventListener(
      'player-proximity-area-changed',
      handleProximityAreaChange as EventListener
    );

    return () => {
      window.removeEventListener(
        'player-proximity-area-changed',
        handleProximityAreaChange as EventListener
      );
    };
  }, [participants, videoDisplayState.audioEnabled, connectionState, isCharacterInitialized, videoDisplayActions, proximityState.inProximityIds]);

  useEffect(() => {
    // Listen for character initialized event
    const handleCharacterInitialized = (playerId: string) => {
      setIsCharacterInitialized(true);
    };

    phaserEvents.on(CHARACTER_INITIALIZED, handleCharacterInitialized);

    // Add manual trigger for debugging
    const triggerManualInit = () => {
      if (!isCharacterInitialized) {
        setIsCharacterInitialized(true);
      }
    };

    // Additional keyboard shortcuts for audio/video
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'c' && e.altKey) {
        triggerManualInit();
      } else if (e.key === 'a' && e.altKey) {
        // Alt+A to toggle audio
        videoDisplayActions.toggleAudio();
      } else if (e.key === 'v' && e.altKey) {
        // Alt+V to toggle video
        videoDisplayActions.toggleVideo();
      } else if (e.key === 's' && e.altKey) {
        // Alt+S to toggle screen sharing
        videoDisplayActions.toggleScreenShare();
      } else if ((e.key === 'm' || e.key === 'µ') && e.altKey) {
        // Alt/Option+M to toggle display mode (macOS produces 'µ' for Option+M)
        videoDisplayActions.toggleDisplayMode();
      } else if (e.key === 'Escape' && videoDisplayState.maximizedScreenShare) {
        // Escape key closes maximized screen share
        videoDisplayActions.minimizeScreenShare();
      } else if (e.key === 't' && e.altKey) {
        // Alt+T to test speaking animations by triggering a custom event
        window.dispatchEvent(new CustomEvent('debug-speaking-test'));
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    // Check if we should manually initialize for debugging
    if (process.env.NODE_ENV === 'development') {
      const timer = setTimeout(() => {
        if (!isCharacterInitialized) {
          setIsCharacterInitialized(true);
        }
      }, 5000);
      return () => {
        clearTimeout(timer);
        phaserEvents.off(CHARACTER_INITIALIZED, handleCharacterInitialized);
        window.removeEventListener('keydown', handleKeyDown);
      };
    }

    return () => {
      phaserEvents.off(CHARACTER_INITIALIZED, handleCharacterInitialized);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [
    isCharacterInitialized,
    videoDisplayActions,
    videoDisplayState.maximizedScreenShare,
  ]);

  // Bug 10 fix: Use refs for connectionState and participants to avoid stale closures in interval
  const connectionStateRef = useRef(connectionState);
  connectionStateRef.current = connectionState;
  const participantsRef = useRef(participants);
  participantsRef.current = participants;

  // Listen for error events from LiveKitRoomConnector
  useEffect(() => {
    const clearTimers = () => {
      if (errorTimerRef.current) {
        clearTimeout(errorTimerRef.current);
        errorTimerRef.current = null;
      }
      if (connectionCheckIntervalRef.current) {
        clearInterval(connectionCheckIntervalRef.current);
        connectionCheckIntervalRef.current = null;
      }
    };

    const handleErrorEvent = (event: CustomEvent<{ message: string }>) => {
      // Store the error message but don't show it immediately
      // Wait 2 seconds before showing the error to allow for transient issues to resolve
      errorTimerRef.current = setTimeout(() => {
        // Bug 10: Read from ref to get current connectionState, not stale closure
        if (!isConnectionStateConnected(connectionStateRef.current)) {
          // Start periodic checks to see if connection is restored
          connectionCheckIntervalRef.current = setInterval(() => {
            // Bug 10: Read from refs to get current values
            if (isConnectionStateConnected(connectionStateRef.current) && participantsRef.current.length > 0) {
              clearTimers();
            }
          }, 3000); // Check every 3 seconds
        }
      }, 2000);
    };

    window.addEventListener('livekit-failsafe-error', handleErrorEvent as EventListener);

    return () => {
      window.removeEventListener('livekit-failsafe-error', handleErrorEvent as EventListener);
      clearTimers();
    };
  }, [connectionState, participants]); // Bug 11: Changed participants.length to participants

  // Track screen share changes and play notification sound
  const previousScreenShareIdsRef = useRef<Set<string>>(new Set());

  // Track which virtual room areas we've already auto-opened screen shares for
  const autoOpenedAreasRef = useRef<Set<string>>(new Set());

  // Local screen share session ID (set via custom event from LiveKitRoomConnector)
  const localScreenShareSessionIdRef = useRef<string>('');

  useEffect(() => {
    const handleSessionId = (event: CustomEvent<{ sessionId: string }>) => {
      localScreenShareSessionIdRef.current = event.detail.sessionId;
    };
    window.addEventListener(LIVEKIT_SCREEN_SHARE_SESSION_ID_EVENT, handleSessionId as EventListener);
    return () => {
      window.removeEventListener(LIVEKIT_SCREEN_SHARE_SESSION_ID_EVENT, handleSessionId as EventListener);
    };
  }, []);

  const getScreenShareSessionId = useCallback((sharerId: string, isLocal: boolean): string => {
    if (isLocal) {
      return localScreenShareSessionIdRef.current || '';
    }
    try {
      const phaserGame = getPhaserGameSync();
      const game = phaserGame?.scene?.keys?.game as Game | undefined;
      const colyseusPlayer = game?.network?.room?.state?.players?.get(sharerId);
      return (colyseusPlayer as any)?.screenShareSessionId || '';
    } catch {
      return '';
    }
  }, []);

  const handleScreenShareClick = useCallback((participantId: string, trackSid: string) => {
    const isLocalParticipant = participants.find((p) => p.identity === participantId)?.isLocal ?? false;
    const sessionId = getScreenShareSessionId(participantId, isLocalParticipant);
    videoDisplayActions.maximizeScreenShare(participantId, trackSid, sessionId);
  }, [participants, getScreenShareSessionId, videoDisplayActions]);

  // Stale track detection and cleanup
  useEffect(() => {
    if (connectionState !== ConnectionState.Connected) {
      return;
    }

    const cleanupInterval = setInterval(() => {
      const phaserGame = getPhaserGameSync();
      if (!phaserGame) return;

      const game = phaserGame.scene?.keys?.game as Game | undefined;
      if (!game || !game.myPlayer) return;

      // Check for stale screen share tracks
      participants.forEach((participant) => {
        if (participant.isLocal) return;

        if (!isParticipantInSameRoom(participant.identity)) {
          // No Phaser / Colyseus / interpolation position — likely stale LiveKit participant
          const screenShareTracks = Array.from(participant.trackPublications.values() as any)
            .filter((pub: any) => pub.source === Track.Source.ScreenShare || pub.source === Track.Source.ScreenShareAudio);

          screenShareTracks.forEach((publication: any) => {
            try {
              if (publication.isSubscribed) {
                publication.setSubscribed(false);
              }
            } catch (error) {
              console.warn(`[LiveKit] Error cleaning up stale screen share track for ${participant.identity}:`, error);
            }
          });
        }
      });
    }, 5000); // Check every 5 seconds

    return () => clearInterval(cleanupInterval);
  }, [participants, connectionState, isParticipantInSameRoom]);

  // Resource monitoring
  useEffect(() => {
    if (connectionState !== ConnectionState.Connected) {
      return;
    }

    const monitorInterval = setInterval(() => {
      const totalTracks = participants.reduce((count, participant) => {
        // Cast to any to avoid type issues with LocalTrackPublication vs RemoteTrackPublication
        const publications = Array.from(participant.trackPublications.values() as any) as any[];
        return count + publications.length;
      }, 0);

      const screenShareTracks = participants.reduce((count, participant) => {
        const screenShares = Array.from(participant.trackPublications.values() as any)
          .filter((pub: any) => pub.source === Track.Source.ScreenShare || pub.source === Track.Source.ScreenShareAudio);
        return count + screenShares.length;
      }, 0);

      // Log resource usage in development mode
      if (process.env.NODE_ENV === 'development' && (totalTracks > 20 || screenShareTracks > 0)) {
        console.log('[LiveKit Resource Monitor]', {
          totalTracks,
          screenShareTracks,
          participants: participants.length,
          timestamp: new Date().toISOString()
        });
      }
    }, 10000); // Monitor every 10 seconds

    return () => clearInterval(monitorInterval);
  }, [participants, connectionState]);

  useEffect(() => {
    const currentScreenShareIds = new Set(
      screenShareTracks.map((track) => track.participant.identity)
    );

    // Find newly started screen shares
    const newScreenShares = Array.from(currentScreenShareIds).filter(
      (id) => !previousScreenShareIdsRef.current.has(id) &&
        isParticipantInSameRoom(id) // CRITICAL: Only same-room participants
    );

    // Filter out shares that are already maximized (to prevent double-sound if track flickers)
    const trulyNewScreenShares = newScreenShares.filter(id => 
      id !== videoDisplayState.maximizedScreenShare?.participantId
    );

    // Play notification sound only for truly new screen shares
    if (trulyNewScreenShares.length > 0) {
      playScreenShareNotification();

      const phaserGame = getPhaserGameSync();
      const game = phaserGame?.scene?.keys?.game as Game | undefined;

      // Check for automatic opening of screen shares in same proximity area
      if (game && game.myPlayer) {
        // When modal is already open: still switch if the local participant started sharing
        if (videoDisplayState.maximizedScreenShare) {
          const localSharerId = trulyNewScreenShares.find((id) =>
            participants.find((p) => p.identity === id)?.isLocal
          );
          if (localSharerId) {
            const screenTrack = screenShareTracks.find(
              (t) => t.participant.identity === localSharerId
            );
            if (screenTrack) {
              const ssid = getScreenShareSessionId(localSharerId, true);
              videoDisplayActions.maximizeScreenShare(
                localSharerId,
                screenTrack.publication?.trackSid || (screenTrack.publication?.track as any)?.sid || '',
                ssid
              );
            }
          }
          previousScreenShareIdsRef.current = currentScreenShareIds;
          return;
        }

        for (const newSharerId of trulyNewScreenShares) {
          const isLocalSharer = participants.find((p) => p.identity === newSharerId)?.isLocal;

          if (isLocalSharer) {
            // Auto-open local screen shares immediately
            const screenTrack = screenShareTracks.find(
              (t) => t.participant.identity === newSharerId
            );

            if (screenTrack) {
              const ssid = getScreenShareSessionId(newSharerId, true);
              videoDisplayActions.maximizeScreenShare(newSharerId, screenTrack.publication?.trackSid || (screenTrack.publication?.track as any)?.sid || '', ssid);
              break;
            }
          } else {
            // For remote participants, they are already filtered by isParticipantInSameRoom
            const screenTrack = screenShareTracks.find(
              (t) => t.participant.identity === newSharerId
            );

            if (screenTrack) {
              const ssid = getScreenShareSessionId(newSharerId, false);
              videoDisplayActions.maximizeScreenShare(newSharerId, screenTrack.publication?.trackSid || (screenTrack.publication?.track as any)?.sid || '', ssid);
              break;
            }
          }
        }
      }
    }

    previousScreenShareIdsRef.current = currentScreenShareIds;
  }, [screenShareTracks, videoDisplayState.maximizedScreenShare, participants, videoDisplayActions, getScreenShareSessionId]);

  // Auto-open screen share modal when entering virtual room area with screen sharing
  useEffect(() => {
    // Don't auto-open if there's already a screen share modal open
    if (videoDisplayState.maximizedScreenShare) {
      return;
    }

    const handleVirtualRoomAreaEnter = (event: any) => {
      // Only handle events for the local player
      const phaserGame = getPhaserGameSync();
      if (!phaserGame) return;

      const game = phaserGame.scene?.keys?.game as Game | undefined;
      if (!game || !game.myPlayer || event.playerId !== game.myPlayer.playerId) {
        return;
      }

      console.log(`[LiveKit] Entered virtual room area: "${event.area?.roomName}", checking for screen sharing participants`);

      // Check if we've already auto-opened for this area
      const areaId = event.area?.roomId;
      if (!areaId) {
        console.log('[LiveKit] No area ID found, skipping auto-open');
        return;
      }

      // Bug 7: Size guard to prevent indefinite growth if leave events are missed
      if (autoOpenedAreasRef.current.size > 50) {
        console.log('[LiveKit] autoOpenedAreasRef exceeded 50 entries, clearing to prevent memory leak');
        autoOpenedAreasRef.current.clear();
      }

      if (autoOpenedAreasRef.current.has(areaId)) {
        console.log(`[LiveKit] Already auto-opened screen share for area "${event.area?.roomName}", skipping`);
        return;
      }

      // Get all participants who are currently screen sharing from the raw participants list
      const screenSharingParticipants = participants
        .filter(p => p.isScreenShareEnabled)
        .map(p => p.identity);

      if (screenSharingParticipants.length === 0) {
        console.log('[LiveKit] No screen sharing participants found in area');
        return;
      }

      // Check if any screen sharing participants are in the same virtual room area
      for (const sharerId of screenSharingParticipants) {
        const isLocalSharer = participants.find((p) => p.identity === sharerId)?.isLocal;

        if (isLocalSharer) {
          // Auto-open local screen shares immediately
          const screenTrack = screenShareTracks.find(
            (t) => t.participant.identity === sharerId
          );
          
          console.log('[LiveKit] Auto-opening local screen share modal in virtual room area');
          const ssid = getScreenShareSessionId(sharerId, true);
          videoDisplayActions.maximizeScreenShare(
            sharerId, 
            screenTrack?.publication?.trackSid || (screenTrack?.publication?.track as any)?.sid || '', 
            ssid
          );
          // Mark this area as auto-opened
          autoOpenedAreasRef.current.add(areaId);
          break;
        } else {
          const resolved = resolveSameVirtualRoomMedia(game, sharerId, virtualRoomFiltersForUi);
          if (resolved.shouldSubscribeMedia) {
            const screenTrack = screenShareTracks.find(
              (t) => t.participant.identity === sharerId
            );

            console.log(`[LiveKit] Auto-opening screen share modal for ${sharerId} in virtual room area "${event.area?.roomName}"`);
            const ssid = getScreenShareSessionId(sharerId, false);
            videoDisplayActions.maximizeScreenShare(
              sharerId, 
              screenTrack?.publication?.trackSid || (screenTrack?.publication?.track as any)?.sid || '', 
              ssid
            );
            autoOpenedAreasRef.current.add(areaId);
            break;
          }
        }
      }
    };

    const handleVirtualRoomAreaLeave = (event: any) => {
      // Only handle events for the local player
      const phaserGame = getPhaserGameSync();
      if (!phaserGame) return;

      const game = phaserGame.scene?.keys?.game as Game | undefined;
      if (!game || !game.myPlayer || event.playerId !== game.myPlayer.playerId) {
        return;
      }

      // Reset auto-opened tracking when leaving an area
      const areaId = event.previousArea?.roomId;
      if (areaId) {
        console.log(`[LiveKit] Left virtual room area: "${event.previousArea?.roomName}", resetting auto-open tracking`);
        autoOpenedAreasRef.current.delete(areaId);
      }
    };

    // Listen for virtual room area enter and leave events
    phaserEvents.on(Event.MY_PLAYER_ENTERED_VIRTUAL_ROOM, handleVirtualRoomAreaEnter);
    phaserEvents.on(Event.MY_PLAYER_LEFT_VIRTUAL_ROOM, handleVirtualRoomAreaLeave);

    return () => {
      phaserEvents.off(Event.MY_PLAYER_ENTERED_VIRTUAL_ROOM, handleVirtualRoomAreaEnter);
      phaserEvents.off(Event.MY_PLAYER_LEFT_VIRTUAL_ROOM, handleVirtualRoomAreaLeave);
      // Bug 7: Clear autoOpenedAreasRef on unmount to prevent memory leak
      autoOpenedAreasRef.current.clear();
    };
  }, [screenShareTracks, videoDisplayState.maximizedScreenShare, participants, videoDisplayActions, virtualRoomFiltersForUi]);

  // Listen for whiteboard-maximize events from the Phaser Whiteboard item
  useEffect(() => {
    const handleMaximize: EventListener = (e) => {
      const detail = (e as CustomEvent).detail
      if (detail?.whiteboardId && detail?.whiteboardUrl) {
        videoDisplayActions.maximizeWhiteboard(detail.whiteboardId, detail.participantId, detail.whiteboardUrl)
        // Immediately mark the local user as active so the badge + auto-close work
        // (the Colyseus schema sync may lag behind this local event)
        if (detail.participantId) {
          whiteboardAddTimestamps.current.set(`${detail.whiteboardId}:${detail.participantId}`, Date.now())
          dispatch(addWhiteboardUser({ whiteboardId: detail.whiteboardId, sessionId: detail.participantId }))
        }
      }
    }
    window.addEventListener('whiteboard-maximize', handleMaximize)
    return () => window.removeEventListener('whiteboard-maximize', handleMaximize)
  }, [videoDisplayActions, dispatch])

  // Track active whiteboard users via ITEM_USER_ADDED / ITEM_USER_REMOVED events.
  // Removals are debounced: if a removal arrives within 3s of the corresponding add,
  // it is treated as a spurious server event and ignored (spurious removals typically
  // arrive within 2-3s). When the initiator explicitly disconnects, WhiteboardModal
  // emits 'whiteboard-local-disconnect' to clear the debounce timestamp so the
  // subsequent server ITEM_USER_REMOVED is processed normally.
  const whiteboardAddTimestamps = useRef(new Map<string, number>())
  useEffect(() => {
    const isWhiteboardType = (itemType: unknown) =>
      itemType === ItemType.WHITEBOARD || itemType === 'WHITEBOARD'

    const handleUserAdded = (userId: string, itemId: string, itemType: unknown, vrId?: string) => {
      // Schema-driven emits don't carry vrId — skip them to avoid overwriting
      // the stored VR mapping with null and double-processing add/remove sequences.
      if (vrId === undefined) return
      if (isWhiteboardType(itemType)) {
        whiteboardAddTimestamps.current.set(`${itemId}:${userId}`, Date.now())
        dispatch(addWhiteboardUser({ whiteboardId: itemId, sessionId: userId }))
        // Store the VR association so badge + auto-open can filter by VR membership
        dispatch(setWhiteboardVrId({ whiteboardId: itemId, vrId }))
        // Ensure a URL exists for this whiteboard ID (remote clients may not have it
        // because WhiteboardHandler stores URLs under schema keys, not client-generated IDs)
        const currentUrls = store.getState().whiteboard.urls
        if (!currentUrls[itemId]) {
          const roomId = store.getState().room.roomId
          if (roomId) {
            dispatch(setWhiteboardUrls({ whiteboardId: itemId, roomId }))
          }
        }
      }
    }
    const handleUserRemoved = (userId: string, itemId: string, itemType: unknown, vrId?: string) => {
      // Schema-driven emits don't carry vrId — skip them (same rationale as handleUserAdded).
      if (vrId === undefined) return
      if (isWhiteboardType(itemType)) {
        const key = `${itemId}:${userId}`
        if (isSpuriousRemoval(whiteboardAddTimestamps.current, key)) {
          return
        }
        whiteboardAddTimestamps.current.delete(key)
        dispatch(removeWhiteboardUser({ whiteboardId: itemId, sessionId: userId }))
      }
    }
    // When the local initiator explicitly disconnects, clear the debounce timestamp
    // so the server's ITEM_USER_REMOVED (which arrives asynchronously) is not blocked.
    const handleLocalDisconnect: EventListener = (e) => {
      const detail = (e as CustomEvent).detail
      if (detail?.whiteboardId && detail?.participantId) {
        const key = `${detail.whiteboardId}:${detail.participantId}`
        whiteboardAddTimestamps.current.delete(key)
      }
    }

    phaserEvents.on(Event.ITEM_USER_ADDED, handleUserAdded)
    phaserEvents.on(Event.ITEM_USER_REMOVED, handleUserRemoved)
    window.addEventListener('whiteboard-local-disconnect', handleLocalDisconnect)
    return () => {
      phaserEvents.off(Event.ITEM_USER_ADDED, handleUserAdded)
      phaserEvents.off(Event.ITEM_USER_REMOVED, handleUserRemoved)
      window.removeEventListener('whiteboard-local-disconnect', handleLocalDisconnect)
    }
  }, [dispatch])

  // Auto-open whiteboard for VR members when someone in the same VR starts using it.
  // Public whiteboards (no vrId) don't auto-open — users walk to them and press R.
  const prevWhiteboardUsersRef = useRef<Record<string, string[]>>({})
  useEffect(() => {
    // Skip if a whiteboard or screenshare is already maximized
    if (videoDisplayState.maximizedWhiteboard || videoDisplayState.maximizedScreenShare) {
      prevWhiteboardUsersRef.current = activeWhiteboardUsers
      return
    }

    const candidate = detectAutoOpenWhiteboard(
      activeWhiteboardUsers,
      prevWhiteboardUsersRef.current,
      whiteboardVrIds,
      localVrId,
    )

    if (candidate) {
      let url = whiteboardUrls[candidate.whiteboardId]
      // Generate URL on-the-fly if not yet in store (remote client ID mismatch)
      if (!url) {
        const roomId = store.getState().room.roomId
        if (roomId) {
          dispatch(setWhiteboardUrls({ whiteboardId: candidate.whiteboardId, roomId }))
          url = store.getState().whiteboard.urls[candidate.whiteboardId]
        }
      }
      if (url) {
        videoDisplayActions.maximizeWhiteboard(candidate.whiteboardId, candidate.newUsers[0], url)
      }
    }

    prevWhiteboardUsersRef.current = activeWhiteboardUsers
  }, [activeWhiteboardUsers, whiteboardUrls, whiteboardVrIds, localVrId, videoDisplayState.maximizedWhiteboard, videoDisplayState.maximizedScreenShare, videoDisplayActions])

  // SC-209: builds the picture-in-picture model and, while a window is open, renders into it. Renders
  // nothing at all otherwise, and nothing here depends on the window existing. Keyed so React keeps the same
  // instance whichever of the two returns below renders it.
  const pipHost = (
    <PipHost
      key="pip-host"
      api={pip}
      participants={participants}
      screenShareTracks={screenShareTracks}
      tracks={tracks}
      speakingIds={proximityState.speakingIds}
      isParticipantVisibleWithGrace={isParticipantVisibleWithGrace}
      isParticipantInSameRoom={isParticipantInSameRoom}
      videoDisplayState={videoDisplayState}
      videoDisplayActions={videoDisplayActions}
      roomName={roomName}
      activeWhiteboardUsers={activeWhiteboardUsers}
      whiteboardVrIds={whiteboardVrIds}
      whiteboardUrls={whiteboardUrls}
      localVrId={localVrId}
      // The call bar's own object, so the window's hand button shows and hides with it.
      raiseHand={controlSurface.raiseHand}
      handRaisedSessionIds={raisedHands.handRaisedSessionIds}
    />
  );

  // Only render when connected and character is initialized
  if (
    connectionState !== ConnectionState.Connected ||
    !isCharacterInitialized ||
    !isVisible ||
    isLoading
  ) {
    // The PiP window outlives this gate (`usePictureInPicture` sits above it), so whatever takes the page
    // down here must not take the window's content with it: a LiveKit (signal-)reconnect, or the loading
    // screen of a Colyseus rejoin while LiveKit stays connected. Unmounting PipHost would blank an open
    // window, drop what it was holding and re-fire the share auto-open on the way back. PipHost renders
    // nothing without a window. Before the character exists there is no call and no window to keep.
    if (isCharacterInitialized) {
      return <RaisedHandsProvider value={raisedHands}>{pipHost}</RaisedHandsProvider>;
    }
    return null;
  }

  // Check which participants are sharing their screen
  const screenShareParticipantIds = new Set(screenShareTracks.map((track) => track.participant.identity));

  return (
    <RaisedHandsProvider value={raisedHands}>
      {/* Error Banner */}
      {videoDisplayState.showErrorBanner && videoDisplayState.errorMessage && (
        <Slide direction="down" in={videoDisplayState.showErrorBanner} mountOnEnter unmountOnExit>
          <Alert
            severity="error"
            sx={{
              position: 'fixed',
              top: 16,
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 9999,
              minWidth: 300,
              maxWidth: 600,
            }}
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() => {
                  // Clear error and show permissions dialog
                  videoDisplayActions.openPermissionsDialog();
                }}
              >
                Fix
              </Button>
            }
          >
            {videoDisplayState.errorMessage}
          </Alert>
        </Slide>
      )}

      {/* Permissions Dialog */}
      <PermissionsDialog
        open={videoDisplayState.showPermissionsDialog}
        onClose={videoDisplayActions.hidePermissionsDialog}
        onPermissionsGranted={videoDisplayActions.handlePermissionsGranted}
      />

      {/* Use our custom proximity-aware audio renderer instead of the default one */}
      <ProximityAwareRoomAudioRenderer isParticipantInSameRoom={isParticipantInSameRoom} />

      {/* Show overlay mode */}
      {videoDisplayState.displayMode === 'overlay' && (
        <>
          {participants
            .filter((participant) => {
              // Always show local participant
              if (participant.isLocal) return true;
              // Keep all same-room participants mounted to avoid React mount/unmount overhead.
              return isParticipantInSameRoom(participant.identity);
            })
            .sort((a, b) => {
              // Sort screen sharing participants first, then local participants
              const aIsScreenSharing = screenShareParticipantIds.has(a.identity);
              const bIsScreenSharing = screenShareParticipantIds.has(b.identity);

              // Screen sharing gets highest priority
              if (aIsScreenSharing && !bIsScreenSharing) return -1;
              if (!aIsScreenSharing && bIsScreenSharing) return 1;

              // Then sort by local vs remote
              if (a.isLocal && !b.isLocal) return -1;
              if (!a.isLocal && b.isLocal) return 1;

              return 0;
            })
            .map((participant) => {
              const isLocal = participant.isLocal;
              const participantId = participant.identity;
              const position = positioningState.playerPositions.get(participantId);
              const isScreenSharing = screenShareParticipantIds.has(participantId);
              const isSpeaking = proximityState.speakingIds.has(participantId);
              const isExpanded = positioningState.expandedVideos.has(participantId);

              const isOverlayVisible = isParticipantVisibleWithGrace(participantId);
              const effectivePosition = position || { x: -9999, y: -9999 };

              return (
                <ParticipantVideoOverlay
                  key={`overlay-${isLocal ? 'local' : 'remote'}-${participantId}`}
                  participantId={participantId}
                  isLocal={isLocal}
                  isSpeaking={isSpeaking}
                  isScreenSharing={isScreenSharing}
                  position={effectivePosition}
                  isVisible={isOverlayVisible}
                  isExpanded={isExpanded}
                  onHover={positioningState.handleVideoHover}
                  onScreenShareClick={handleScreenShareClick}
                  videoControls={{
                    ...videoDisplayState,
                    ...videoDisplayActions,
                  }}
                  participants={participants}
                  screenShareTracks={screenShareTracks}
                  cameraTrack={tracks.find(t => t.participant.identity === participantId)}
                  ownerSessionId={ownerSessionId}
                  isUsingWhiteboard={whiteboardUserSessionIds.has(participantId)}
                  onWhiteboardClick={handleWhiteboardClick}
                  isHandRaised={raisedHands.handRaisedSessionIds.has(participantId)}
                  onLowerHand={isLocal ? undefined : lowerOthersHand}
                  ref={(el) => {
                    if (el) {
                      const existingElement = positioningState.videoElementsRef.current.get(participantId);
                      // Evict stale ref to a detached node (e.g. participant was
                      // filtered out by isParticipantInSameRoom, overlay unmounted,
                      // but the participant still exists in the LiveKit room).
                      if (existingElement && !existingElement.isConnected) {
                        positioningState.videoElementsRef.current.delete(participantId);
                      }
                      if (positioningState.videoElementsRef.current.get(participantId) !== el) {
                        // New or replaced element — register and set initial position.
                        positioningState.videoElementsRef.current.set(participantId, el);
                        positioningState.updatePositionWithRef(el, effectivePosition);
                      }
                      // Skip position update on re-renders of the same element:
                      // effectivePosition comes from React state which is only
                      // snapshot when participants enter/leave, so it can be stale.
                      // The Phaser scene-update callback applies the correct
                      // position imperatively every frame.
                    }
                    // NOTE: Do NOT delete from videoElementsRef on the null callback.
                    // Inline ref callbacks fire null→el on every re-render (new function
                    // identity). Deleting on null would defeat the same-element guard
                    // above, causing stale effectivePosition to be re-applied on each
                    // speaking-triggered re-render.
                  }}
                />
              );
            })}
        </>
      )}

      {/* SC-375: gallery mode */}
      {videoDisplayState.displayMode === 'gallery' && (
        <GalleryVideoDisplay
          participants={participants}
          screenShareTracks={screenShareTracks}
          tracks={tracks}
          speakingIds={proximityState.speakingIds}
          isParticipantVisibleWithGrace={isParticipantVisibleWithGrace}
          isParticipantInSameRoom={isParticipantInSameRoom}
          handleScreenShareClick={handleScreenShareClick}
          videoDisplayState={videoDisplayState}
          videoDisplayActions={videoDisplayActions}
          ownerSessionId={ownerSessionId}
          whiteboardUserSessionIds={whiteboardUserSessionIds}
          onWhiteboardClick={handleWhiteboardClick}
          handRaisedSessionIds={raisedHands.handRaisedSessionIds}
          onLowerHand={lowerOthersHand}
          onBackToMap={videoDisplayActions.leaveGallery}
          onVisibleTileCountChange={setGalleryTileCount}
        />
      )}

      {/* Show sidebar/top mode */}
      {videoDisplayState.displayMode !== 'overlay' && videoDisplayState.displayMode !== 'gallery' && (
        <SidebarVideoDisplay
          participants={participants}
          screenShareTracks={screenShareTracks}
          tracks={tracks}
          speakingIds={proximityState.speakingIds}
          isParticipantVisibleWithGrace={isParticipantVisibleWithGrace}
          isParticipantInSameRoom={isParticipantInSameRoom}
          handleScreenShareClick={handleScreenShareClick}
          videoDisplayState={videoDisplayState}
          videoDisplayActions={videoDisplayActions}
          ownerSessionId={ownerSessionId}
          panelPosition={
            videoDisplayState.displayMode === 'sidebar-right' ? 'right'
              : videoDisplayState.displayMode === 'top' ? 'top'
              : 'left'
          }
          whiteboardUserSessionIds={whiteboardUserSessionIds}
          onWhiteboardClick={handleWhiteboardClick}
          handRaisedSessionIds={raisedHands.handRaisedSessionIds}
          onLowerHand={lowerOthersHand}
        />
      )}

      {/* Render the screen share modal from the separate component */}
      {videoDisplayState.maximizedScreenShare && (
        <ScreenShareModal
          maximizedScreenShare={videoDisplayState.maximizedScreenShare}
          onClose={videoDisplayActions.minimizeScreenShare}
          onToggleScreenShare={videoDisplayActions.toggleScreenShare}
          screenShareEnabled={videoDisplayState.screenShareEnabled}
          toggleAudio={videoDisplayActions.toggleAudio}
          toggleVideo={videoDisplayActions.toggleVideo}
          audioEnabled={videoDisplayState.audioEnabled}
          videoEnabled={videoDisplayState.videoEnabled}
          onSwitchScreenShare={videoDisplayActions.maximizeScreenShare}
          onChangeScreenShareSource={videoDisplayActions.changeScreenShareSource}
          pictureInPicture={controlSurface.pictureInPicture}
        />
      )}

      {/* Render the whiteboard modal */}
      {videoDisplayState.maximizedWhiteboard && (
        <WhiteboardModal
          whiteboardId={videoDisplayState.maximizedWhiteboard.whiteboardId}
          whiteboardUrl={videoDisplayState.maximizedWhiteboard.whiteboardUrl}
          participantId={videoDisplayState.maximizedWhiteboard.participantId}
          onClose={videoDisplayActions.minimizeWhiteboard}
          toggleAudio={videoDisplayActions.toggleAudio}
          toggleVideo={videoDisplayActions.toggleVideo}
          audioEnabled={videoDisplayState.audioEnabled}
          videoEnabled={videoDisplayState.videoEnabled}
          pictureInPicture={controlSurface.pictureInPicture}
        />
      )}

      {pipHost}
    </RaisedHandsProvider>
  );
}; 
